import { Link } from "react-router";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { brandColors as colors, usePalette } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { fadeUp, stagger } from "@/lib/animations";
import type { GuideCard as GuideCardType, GuideTopicCard } from "@/lib/guides";
import Footer from "@/components/ui/Footer";
import Blob from "@/components/ui/Blob";
import WaveDivider from "@/components/ui/WaveDivider";
import GuideCard from "@/components/guides/GuideCard";

type Props = {
  topics: GuideTopicCard[];
  guides: GuideCardType[];
};

export default function GuidesIndexPage({ topics, guides }: Props) {
  const theme = usePalette();
  const featured = guides.filter((g) => g.featured).slice(0, 3);
  const rest = guides.filter((g) => !g.featured);

  return (
    <div className="min-h-dvh" style={{ backgroundColor: colors.cream }}>

      {/* Hero */}
      <section
        className="relative overflow-hidden pt-32 pb-20"
        style={{ backgroundColor: colors.cream }}
      >
        <Blob className="w-96 h-96 -top-20 -left-10" color={theme.blob1} />
        <Blob className="w-72 h-72 top-20 right-0" color={theme.blob3} />

        <div className="relative z-10 mx-auto max-w-5xl px-6 text-center">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-3 text-xs uppercase"
            style={{
              fontFamily: fonts.body,
              fontWeight: 800,
              letterSpacing: "0.2em",
              color: theme.accent,
            }}
          >
            How I do it
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="text-5xl sm:text-6xl lg:text-7xl"
            style={{
              fontFamily: fonts.logo,
              fontWeight: 400,
              color: colors.navy,
              lineHeight: 1.1,
            }}
          >
            Little{" "}
            <span style={{ fontFamily: fonts.heading, color: theme.warm }}>
              how-to&rsquo;s
            </span>{" "}
            from my world
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mx-auto mt-6 max-w-2xl text-base"
            style={{
              fontFamily: fonts.body,
              fontWeight: 500,
              color: `${colors.navy}b3`,
              letterSpacing: "0.03em",
              lineHeight: 1.7,
            }}
          >
            Sewing patterns, hair, home — short videos, written steps, and the actual
            products I reach for. Bookmark the ones that help.
          </motion.p>
        </div>
      </section>

      {/* Topic chips */}
      {topics.length > 0 && (
        <section className="pb-12" style={{ backgroundColor: colors.cream }}>
          <div className="mx-auto max-w-5xl px-6">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
              className="flex flex-wrap justify-center gap-3"
            >
              {topics.map((t, i) => (
                <motion.div key={t.id} variants={fadeUp} custom={i}>
                  <Link
                    to={`/guides/${t.slug}`}
                    className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 transition-all hover:-translate-y-0.5 hover:shadow-md"
                    style={{
                      backgroundColor: t.color ? `${t.color}1f` : `${theme.accent}14`,
                      color: t.color ?? theme.accent,
                      fontFamily: fonts.body,
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                      fontSize: "0.8rem",
                      textTransform: "uppercase",
                      border: `1px solid ${t.color ?? theme.accent}33`,
                    }}
                  >
                    <Sparkles size={14} />
                    {t.name}
                    <span
                      className="rounded-full px-2 py-0.5 text-[10px]"
                      style={{
                        backgroundColor: colors.cream,
                        color: colors.navy,
                        fontWeight: 700,
                      }}
                    >
                      {t.guideCount}
                    </span>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* Featured */}
      {featured.length > 0 && (
        <>
          <WaveDivider color={theme.sectionBg} />
          <section
            className="py-20"
            style={{ backgroundColor: theme.sectionBg }}
          >
            <div className="mx-auto max-w-6xl px-6">
              <p
                className="mb-2 text-xs uppercase"
                style={{
                  fontFamily: fonts.body,
                  fontWeight: 800,
                  letterSpacing: "0.2em",
                  color: theme.accent,
                }}
              >
                Hand-picked
              </p>
              <h2
                className="mb-10 text-3xl lg:text-4xl"
                style={{ fontFamily: fonts.logo, color: colors.navy, fontWeight: 400 }}
              >
                The{" "}
                <span style={{ fontFamily: fonts.heading, color: theme.warm }}>
                  favourites
                </span>
              </h2>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                variants={stagger}
                className="grid gap-6 md:grid-cols-3"
              >
                {featured.map((g, i) => (
                  <GuideCard key={g.id} guide={g} index={i} />
                ))}
              </motion.div>
            </div>
          </section>
          <WaveDivider color={colors.cream} />
        </>
      )}

      {/* All guides */}
      <section className="py-20" style={{ backgroundColor: colors.cream }}>
        <div className="mx-auto max-w-6xl px-6">
          <div className="mb-10 flex items-end justify-between gap-4">
            <div>
              <p
                className="mb-2 text-xs uppercase"
                style={{
                  fontFamily: fonts.body,
                  fontWeight: 800,
                  letterSpacing: "0.2em",
                  color: theme.accent,
                }}
              >
                The library
              </p>
              <h2
                className="text-3xl lg:text-4xl"
                style={{ fontFamily: fonts.logo, color: colors.navy, fontWeight: 400 }}
              >
                Every guide
              </h2>
            </div>
            <Link
              to="/#newsletter"
              className="hidden items-center gap-2 text-sm sm:inline-flex"
              style={{
                fontFamily: fonts.body,
                fontWeight: 700,
                color: theme.accent,
                letterSpacing: "0.05em",
              }}
            >
              Get new ones in your inbox <ArrowRight size={14} />
            </Link>
          </div>

          {rest.length === 0 && featured.length === 0 ? (
            <div
              className="rounded-3xl p-12 text-center"
              style={{
                backgroundColor: theme.sectionBg,
                border: `1px dashed ${colors.tan}55`,
                fontFamily: fonts.body,
                color: `${colors.navy}aa`,
              }}
            >
              First guides are on the way. In the meantime, subscribe below.
            </div>
          ) : (
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={stagger}
              className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
            >
              {(rest.length ? rest : guides).map((g, i) => (
                <GuideCard key={g.id} guide={g} index={i} />
              ))}
            </motion.div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
